import React, {useRef, useEffect, useState} from "react";
import BaseModal from "../../modals/base-modal";
import FormField from "../../forms/form-field";
import FormSelect from "../../forms/form-select";
import InlineModal from "../../info-modal/infoModal";
import {instanceMob} from "../../../utils/utils";
import {getBackendError} from "../../../utils/error-helpers";
import {Button} from "react-bootstrap";
import useLocationStore from "../config/use-location-store";
import Tooltip from "../../tooltip/tooltip";

const PartnerLoanModal = ({show, onClose}) => {
    const formRef = useRef(null);
    const [infoModal, setInfoModal] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [partners, setPartners] = useState([]);

    const [partnerMap, setPartnerMap] = useState({});

    const [selectedPartner, setSelectedPartner] = useState("");
    const [selectedPartnerEmail, setSelectedPartnerEmail] = useState("");
    const [selectedPartnerName, setSelectedPartnerName] = useState("");

    const [amount, setAmount] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [receiptNumber, setReceiptNumber] = useState("");
    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const cleanCurrencyInput = (value) => {
        const cleaned = value.replace(/[^0-9.]/g, '');
        const parts = cleaned.split('.');
        if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('');
        return cleaned;
    };

    const handleAmountChange = (e) => {
        const value = cleanCurrencyInput(e.target.value);
        if (value.startsWith('-')) return;
        setAmount(value);
    };

    const handlePartnerChange = (e) => {
        const partnerId = e.target.value;
        setSelectedPartner(partnerId);

        const partnerData = partnerMap[partnerId];
        if (partnerData) {
            setSelectedPartnerEmail(partnerData.email);
            setSelectedPartnerName(partnerData.name);
        } else {
            setSelectedPartnerEmail("");
            setSelectedPartnerName("");
        }
    };

    const fetchPartners = () => {
        setIsLoading(true);
        instanceMob
            .get("admin/customers/elevva-admin")
            .then((res) => {
                const partnerMapData = {};

                const partnersData = res.data.map((p) => {
                    const id = String(p.user_id);

                    partnerMapData[id] = {
                        email: p.email,
                        name: p.full_name
                    };

                    return {
                        value: id,
                        label: `${p.full_name} (${p.email})`,
                    };
                });

                setPartners(partnersData);
                setPartnerMap(partnerMapData);
            })
            .catch((err) => {
                console.error("Error cargando socios:", err);
                setInfoModal({
                    iconType: "error",
                    title: "Error de Carga",
                    description: "No se pudieron cargar los socios. Intenta de nuevo más tarde.",
                    secondaryAction: {label: "Cerrar", onClick: () => setInfoModal(null)},
                });
            })
            .finally(() => setIsLoading(false));
    };

    const fetchRegisterNumber = () => {
        if (!selectedLocation) return;
        instanceMob
            .get(`admin/accounting/register-number?location_id=${selectedLocation.id}`)
            .then((res) => {
                const number = res.data?.next_registry_number || "";
                setReceiptNumber(number);
            })
            .catch((err) => {
                console.error("Error cargando número de registro:", err);
            });
    };

    useEffect(() => {
        if (show) {
            fetchPartners()
            fetchRegisterNumber();
        }
        if (!show) {
            setSelectedPartner("");
            setSelectedPartnerEmail("");
            setSelectedPartnerName("");
            setAmount("");
        }
    }, [show]);

    const formFields = [
        {
            Component: FormField,
            label: "# Registro",
            name: "registry_number",
            type: "hidden",
            value: receiptNumber,
        },
        {
            Component: FormField,
            label: "Fecha",
            name: "date",
            type: "text",
            value: new Date().toLocaleDateString("es-CO", {day: '2-digit', month: '2-digit', year: '2-digit'}),
            readOnly: true,
            tooltip: "Fecha actual del registro del préstamo."
        },
        {
            Component: FormSelect,
            label: "Socio",
            name: "partner_id",
            options: partners,
            placeholder: "Selecciona el socio",
            searchable: true,
            isLoading: isLoading,
            required: true,
            value: selectedPartner || "",
            onChange: handlePartnerChange,
            tooltip: "Selecciona del listado el socio al que se le realizará el préstamo o escribe su nombre para encontrarlo rápidamente.",
        },
        {
            Component: FormField,
            label: "Valor",
            name: "value",
            type: "currency",
            placeholder: "Escribe aquí",
            required: true,
            isLoading: isLoading,
            value: amount === "" ? undefined : amount,
            onChange: handleAmountChange,
            tooltip: "Ingresa el monto total del préstamo que se entregará al socio.",
        },
        {
            Component: FormField,
            label: "Descripción",
            name: "description",
            type: "textarea",
            placeholder: "Préstamo",
            isLoading: isLoading,
            required: true,
            tooltip: "Detalla el motivo o concepto del préstamo otorgado al socio.",
        },
    ];

    const handleSave = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData(formRef.current);
        const formPayload = Object.fromEntries(formData.entries());

        const partnerId = formPayload.partner_id;
        const loanAmount = Number(amount);

        if (!partnerId) {
            setIsSubmitting(false);
            return setInfoModal({
                iconType: "warning",
                title: "Campo requerido",
                description: "Por favor, selecciona un socio.",
                secondaryAction: {label: "Cerrar", onClick: () => setInfoModal(null)},
            });
        }
        if (loanAmount <= 0 || isNaN(loanAmount)) {
            setIsSubmitting(false);
            return setInfoModal({
                iconType: "warning",
                title: "Valor Inválido",
                description: "El Valor del préstamo debe ser un número positivo.",
                secondaryAction: {label: "Cerrar", onClick: () => setInfoModal(null)},
            });
        }
        if (!selectedPartnerEmail || !selectedPartnerName) {
            setIsSubmitting(false);
            return setInfoModal({
                iconType: "error",
                title: "Error de Socio",
                description: "Faltan datos (email/nombre) del socio seleccionado.",
                secondaryAction: {label: "Cerrar", onClick: () => setInfoModal(null)},
            });
        }

        const finalPayload = {
            subject_name: selectedPartnerName,
            partner_id: Number(partnerId),
            subject_email: selectedPartnerEmail,
            location_id: Number(selectedLocation?.id),
            value: loanAmount,
            description: formPayload.description,
            registry_number: formPayload.registry_number,
            payment_method: 30
        };

        instanceMob
            .post("admin/accounting/loan/create", finalPayload)
            .then(() => {
                setInfoModal({
                    iconType: "success",
                    title: "Préstamo Registrado",
                    description: "El préstamo al socio ha sido registrado con éxito.",
                    primaryAction: {
                        label: "Aceptar",
                        onClick: () => {
                            setInfoModal(null);
                            onClose();
                        },
                    },
                });
            })
            .catch((err) => {
                const errorMessage = getBackendError(err);
                setInfoModal({
                    iconType: "error",
                    title: "Error al Guardar",
                    description: errorMessage,
                    secondaryAction: {label: "Cerrar", onClick: () => setInfoModal(null)},
                });
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <>
            <BaseModal
                show={show}
                onClose={onClose}
                title="Préstamo a Socios"
                size={"lg"}
                showFooter={false}
            >
                <form ref={formRef} className="d-flex flex-column gap-3" onSubmit={handleSave}>
                    {formFields.map((field, index) => {
                        const FieldComponent = field.Component;

                        const inputElement = (
                            <FieldComponent
                                key={index}
                                id={field.name}
                                {...field}
                            />
                        );

                        if (field.tooltip && field.type !== "hidden") {
                            return (
                                <Tooltip
                                    key={index}
                                    title={field.label}
                                    description={field.tooltip}
                                    position="right"
                                    variant="rich"
                                >
                                    {inputElement}
                                </Tooltip>
                            );
                        }

                        return inputElement;
                    })}
                </form>

                <BaseModal.Footer>
                    <Button
                        className="base-modal-cancel-button"
                        variant="secondary"
                        onClick={onClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        className="base-modal-done-button"
                        variant="primary"
                        onClick={handleSave}
                        disabled={isSubmitting || isLoading || !selectedPartner}
                    >
                        {isSubmitting ? "..." : "Guardar"}
                    </Button>
                </BaseModal.Footer>
            </BaseModal>

            {infoModal && (
                <InlineModal
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default PartnerLoanModal;